import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { LiftPickerSheet } from './LiftPickerSheet';
import { api, apiForm, ApiError } from '../../lib/api';
import { useAiDailyQuota } from '../../lib/aiDailyQuota';
import { localDateKey } from '../../lib/dateKey';
import { type LiftDef, liftHintForUpload } from '../../lib/liftPicker';
import { btnGhost, btnPrimary } from '../../lib/uiClasses';

type FormAnalysisMockResult = {
  lift: string;
  findings: Array<{ severity: string; cue: string; detail: string }>;
  summary: string;
  nextSteps: string[];
};

const MAX_UPLOAD_BYTES = 40 * 1024 * 1024;

function errMessage(e: unknown, fallback: string, step: 'upload' | 'analyze'): string {
  if (e instanceof ApiError) {
    if (e.body) {
      try {
        const j = JSON.parse(e.body) as { error?: string };
        if (j?.error) return j.error;
      } catch {
        /* ignore */
      }
    }
    if (e.status === 413) {
      return 'Video rejected as too large by the server. Trim to ~10s or ask support to raise the upload limit.';
    }
    if (e.status === 0 || /nginx body-size|connection reset/i.test(e.message)) {
      return e.message;
    }
    if (e.message && !/^\s*</.test(e.message)) return e.message;
  }
  const msg = e instanceof Error ? e.message : '';
  if (/load failed|failed to fetch|networkerror|network request failed|aborted/i.test(msg)) {
    return step === 'upload'
      ? 'Upload connection dropped. Usually the server upload limit is too low (nginx). Use a 720p/short clip, or raise client_max_body_size.'
      : 'Analyze connection dropped — the server likely timed out. Try again; we are shortening analysis time.';
  }
  if (/timeout/i.test(msg)) {
    return step === 'upload' ? 'Upload timed out. Try a shorter clip.' : 'Analyze timed out. Try again in a moment.';
  }
  return msg || fallback;
}

export function CoachFormCheck({
  userId,
  onError,
}: {
  userId?: string;
  onError: (msg: string | null) => void;
}) {
  const [lift, setLift] = useState<LiftDef | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'uploading' | 'ready' | 'error'>('idle');
  const [analyzeBusy, setAnalyzeBusy] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [formResult, setFormResult] = useState<FormAnalysisMockResult | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const { quota, refresh, setQuota } = useAiDailyQuota(true);
  const formCap = !quota?.unlimited && (quota?.formChecks.remaining ?? 1) <= 0;

  const localPreviewRef = useRef<string | null>(null);
  const formFileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const stagedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    };
  }, []);

  useEffect(() => {
    if (!formResult) return;
    requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [formResult]);

  useEffect(() => {
    if (uploadPhase !== 'ready' && uploadPhase !== 'uploading') return;
    if (!localPreviewUrl) return;
    requestAnimationFrame(() => {
      stagedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [uploadPhase, localPreviewUrl]);

  function setLocalErrMsg(msg: string | null) {
    setLocalErr(msg);
    // Keep parent banner clear so errors stay next to form check.
    onError(null);
  }

  function setLocalPreview(file: File) {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    const url = URL.createObjectURL(file);
    localPreviewRef.current = url;
    setLocalPreviewUrl(url);
  }

  function scrollTo(el: HTMLElement | null) {
    requestAnimationFrame(() => {
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  async function uploadFile(file: File) {
    setUploadPhase('uploading');
    setUploadId(null);
    setFormResult(null);
    setLocalErrMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiForm<{ id: string; url: string; status?: string }>('/api/media/form-video', fd);
      setUploadId(res.id);
      setUploadPhase('ready');
      scrollTo(stagedRef.current);
    } catch (e) {
      setUploadPhase('error');
      setLocalErrMsg(errMessage(e, 'Could not upload video.', 'upload'));
    }
  }

  function onFileChosen(f: File | null) {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      setLocalErrMsg(
        `That video is ${(f.size / (1024 * 1024)).toFixed(0)} MB — max is 40 MB. Record in 720p or trim to ~10s.`,
      );
      return;
    }
    setLift(null);
    setFormResult(null);
    setPlaybackError(null);
    setLocalPreview(f);
    scrollTo(stagedRef.current);
    void uploadFile(f);
  }

  function onLiftPicked(liftDef: LiftDef) {
    setLift(liftDef);
    setLocalErrMsg(null);
  }

  async function runAnalyze() {
    if (!uploadId) {
      setLocalErrMsg('Wait for the green check — video is still uploading.');
      return;
    }
    if (!lift) {
      setPickerOpen(true);
      setLocalErrMsg('Pick an exercise first.');
      return;
    }
    setAnalyzeBusy(true);
    setLocalErrMsg(null);
    setFormResult(null);
    try {
      const res = await api<{ mockResult: FormAnalysisMockResult; quota?: typeof quota }>(
        `/api/media/form-video/${uploadId}/analyze`,
        {
          method: 'POST',
          body: JSON.stringify({ lift: liftHintForUpload(lift), dateKey: localDateKey() }),
        },
      );
      setFormResult(res.mockResult);
      if (res.quota) setQuota(res.quota);
      else void refresh();
      scrollTo(resultRef.current);
    } catch (e) {
      setLocalErrMsg(errMessage(e, 'Could not analyze video.', 'analyze'));
      void refresh();
    } finally {
      setAnalyzeBusy(false);
    }
  }

  function clearVideo() {
    if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    localPreviewRef.current = null;
    setLocalPreviewUrl(null);
    setUploadId(null);
    setUploadPhase('idle');
    setLift(null);
    setFormResult(null);
    setLocalErrMsg(null);
    setPlaybackError(null);
  }

  const canAnalyze = uploadPhase === 'ready' && !!uploadId && !!lift && !analyzeBusy && !formCap;

  return (
    <section id="form-check" className="scroll-mt-8">
      <p className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Form check</p>
      {quota ? (
        <p className="mb-3 text-xs text-[var(--text-muted)]">
          {quota.unlimited
            ? 'Unlimited form checks on this account.'
            : `${quota.formChecks.remaining} of ${quota.formChecks.limit} form checks left today.`}
        </p>
      ) : null}

      <input ref={formFileInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)} />
      <input
        ref={cameraInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
      />

      {!localPreviewUrl ? (
        <motion.button
          type="button"
          onClick={() => formFileInputRef.current?.click()}
          whileHover={{ scale: 1.005 }}
          className="group flex w-full flex-col items-center justify-center rounded-3xl border border-dashed border-white/15 bg-[#121827] px-4 py-10 text-center transition-all hover:border-[#4F8CFF]/35 hover:shadow-[0_0_40px_rgba(79,140,255,0.08)]"
        >
          <div className="flex gap-4 text-[var(--text-muted)]">
            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <path d="M12 16V8m0 0 3 3m-3-3-3 3" strokeLinecap="round" />
              <rect x="3" y="5" width="18" height="14" rx="2" />
            </svg>
            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <path d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 8h8a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2z" strokeLinecap="round" />
            </svg>
          </div>
          <p className="mt-4 text-sm font-semibold text-[var(--text-primary)]">Drop video</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">or tap to upload from your library</p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              cameraInputRef.current?.click();
            }}
            className="mt-4 rounded-full border border-white/10 bg-white/[0.05] px-4 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:border-[#4F8CFF]/30 hover:text-[#4F8CFF]"
          >
            Record now
          </button>
        </motion.button>
      ) : (
        <div ref={stagedRef} className="space-y-3">
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 px-3.5 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              {uploadPhase === 'ready' ? (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white"
                  aria-hidden
                >
                  ✓
                </span>
              ) : uploadPhase === 'uploading' ? (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-emerald-400/40 text-xs text-emerald-200">
                  …
                </span>
              ) : (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/80 text-sm font-bold text-white">
                  !
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-emerald-100">
                  {uploadPhase === 'ready'
                    ? 'Video uploaded'
                    : uploadPhase === 'uploading'
                      ? 'Uploading…'
                      : 'Upload failed'}
                </p>
                <p className="text-[11px] text-emerald-200/70">
                  {uploadPhase === 'ready' ? 'Pick an exercise, then analyze.' : 'Keep this screen open while it finishes.'}
                </p>
              </div>
            </div>
            <button type="button" onClick={clearVideo} className={`${btnGhost} !h-8 shrink-0 !px-3 !text-xs`}>
              Change
            </button>
          </div>

          <video
            key={localPreviewUrl}
            src={localPreviewUrl}
            controls
            playsInline
            preload="metadata"
            className="aspect-video w-full max-h-[min(40vh,280px)] rounded-2xl border border-white/10 bg-black object-contain"
            onError={() => setPlaybackError('Preview could not play on this device — you can still analyze.')}
          />
          {playbackError ? <p className="text-xs text-amber-200/90">{playbackError}</p> : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={uploadPhase === 'uploading'}
              onClick={() => setPickerOpen(true)}
              className={`${btnGhost} !text-sm`}
            >
              {lift ? `Exercise: ${lift.label}` : 'Choose exercise'}
            </button>
            {lift ? (
              <button type="button" onClick={() => setPickerOpen(true)} className="text-xs text-[var(--text-muted)] underline-offset-2 hover:underline">
                Edit
              </button>
            ) : null}
          </div>

          <button
            type="button"
            disabled={!canAnalyze}
            onClick={() => void runAnalyze()}
            className={`${btnPrimary} w-full disabled:opacity-40`}
          >
            {analyzeBusy ? 'Analyzing form…' : formCap ? 'Daily form-check limit reached' : 'Analyze'}
          </button>
          {uploadPhase === 'ready' && !lift ? (
            <p className="text-center text-[11px] text-[var(--text-muted)]">Choose an exercise to enable Analyze.</p>
          ) : null}
          {uploadPhase === 'error' ? (
            <button type="button" onClick={() => formFileInputRef.current?.click()} className={`${btnGhost} w-full !text-sm`}>
              Try upload again
            </button>
          ) : null}
        </div>
      )}

      <p className="mt-3 text-[11px] text-[var(--text-muted)]">
        Best results: 10–20 second clip, side or 45° angle.
      </p>

      {localErr ? (
        <p className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{localErr}</p>
      ) : null}

      {formResult ? (
        <motion.div
          ref={resultRef}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 space-y-2"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-muted)]">AI form feedback</p>
          <div className="rounded-2xl border border-white/8 bg-white/[0.04] px-3.5 py-3">
            <p className="text-sm text-[var(--text-secondary)]">{formResult.summary}</p>
          </div>
          {formResult.findings.slice(0, 4).map((f, i) => (
            <div key={i} className="rounded-2xl border border-white/8 bg-white/[0.03] px-3.5 py-2.5">
              <p className="text-sm font-medium text-[var(--text-primary)]">{f.cue}</p>
              <p className="mt-0.5 text-xs text-[var(--text-muted)]">{f.detail}</p>
            </div>
          ))}
        </motion.div>
      ) : null}

      <LiftPickerSheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onLiftPicked}
        selectedId={lift?.id}
        userId={userId}
      />
    </section>
  );
}
